import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import StatusBadge from '../components/StatusBadge';
import ChangeRequestForm from '../components/ChangeRequestForm';
import ChangeHistory from '../components/ChangeHistory';
import TimeTab from '../components/TimeTab';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ClaimCore {
  claim_id: number;
  fh_claim_num: string;
  tier_claim_num: string;
  open_close_status: string;
  loss_date: string | null;
  received_date: string | null;
  closed_date: string | null;
  reopened_date: string | null;
  handler_name: string;
  company_name: string;
  coverage_type_code: string;
  insured_name: string;
  defense_counsel: string | null;
  in_litigation_code: string | null;
  deductible_amt: number | null;
  reserve_fees: number | null;
  lob_id: string | null;
  controlling_state: string | null;
}

interface Financials {
  total_outstanding: number;
  total_paid: number;
  total_recovered: number;
  total_incurred: number;
}

interface ReservePaymentRow {
  type_code: string;
  reserve_type: string;
  description: string;
  loss_amount: number;
  alae_amount: number;
  transaction_count: number;
}

interface Transaction {
  date: string;
  type_code: string;
  description: string;
  amount: number;
  loss_amount: number;
  alae_amount: number;
}

interface Party {
  party_id: number;
  first_name: string;
  last_name: string;
  city: string | null;
  state: string | null;
}

interface Policy {
  policy_number: string;
  effective_date: string | null;
  expiration_date: string | null;
  policy_limit: number | null;
  deductible: number | null;
}

interface DiaryEntry {
  diary_date: string;
  diary_type: string;
  description: string;
  completed_date: string | null;
  assigned_to: string | null;
}

interface ClaimDetailData {
  claim: ClaimCore;
  financials: Financials;
  reserves: ReservePaymentRow[];
  payments: ReservePaymentRow[];
  transactions: Transaction[];
  parties: Party[];
  policy: Policy | null;
  diary: DiaryEntry[];
}

interface AiResult {
  text: string;
  model: string;
  latency_ms: number;
  mock_mode: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

function formatAmount(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

// Render simple markdown-like text: **bold** headers, newline splitting
function AiTextBlock({ text }: { text: string }) {
  const lines = text.split('\n');
  return (
    <div className="space-y-1 text-sm text-gray-700">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={i} className="h-2" />;

        // Bold header: line starts and ends with **
        if (trimmed.startsWith('**') && trimmed.endsWith('**') && trimmed.length > 4) {
          return (
            <p key={i} className="font-semibold text-gray-900 mt-3">
              {trimmed.slice(2, -2)}
            </p>
          );
        }

        // Inline bold replacement
        const parts = trimmed.split(/(\*\*[^*]+\*\*)/g);
        return (
          <p key={i}>
            {parts.map((part, j) => {
              if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={j}>{part.slice(2, -2)}</strong>;
              }
              return <span key={j}>{part}</span>;
            })}
          </p>
        );
      })}
    </div>
  );
}

// ─── Tab Types ────────────────────────────────────────────────────────────────

type TabId = 'overview' | 'financials' | 'ai' | 'parties' | 'diary' | 'changes' | 'time';

const TABS: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'financials', label: 'Financials' },
  { id: 'ai', label: 'AI Analysis' },
  { id: 'parties', label: 'Parties' },
  { id: 'diary', label: 'Diary' },
  { id: 'changes', label: 'Changes' },
  { id: 'time', label: 'Time' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function FinancialCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-gray-900">{formatCurrency(value)}</p>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="py-2 border-b border-gray-100 last:border-0">
      <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900">{value || '—'}</dd>
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
      {children}
    </h3>
  );
}

function TableContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">{children}</table>
    </div>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th
      scope="col"
      className={`px-4 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide ${
        right ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}

function Td({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <td className={`px-4 py-2.5 whitespace-nowrap text-gray-700 ${right ? 'text-right' : ''}`}>
      {children}
    </td>
  );
}

// ─── Tab: Overview ────────────────────────────────────────────────────────────

function OverviewTab({ claim, policy }: { claim: ClaimCore; policy: Policy | null }) {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <SectionHeading>Claim Details</SectionHeading>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          <dl>
            <DetailRow label="Loss Date" value={formatDate(claim.loss_date)} />
            <DetailRow label="Received Date" value={formatDate(claim.received_date)} />
            <DetailRow label="Closed Date" value={formatDate(claim.closed_date)} />
            <DetailRow label="Reopened Date" value={formatDate(claim.reopened_date)} />
            <DetailRow label="Coverage Type" value={claim.coverage_type_code} />
            <DetailRow label="Line of Business" value={claim.lob_id} />
          </dl>
          <dl>
            <DetailRow label="Controlling State" value={claim.controlling_state} />
            <DetailRow label="Insured Name" value={claim.insured_name} />
            <DetailRow label="Defense Counsel" value={claim.defense_counsel} />
            <DetailRow label="Company" value={claim.company_name} />
            <DetailRow
              label="In Litigation"
              value={
                claim.in_litigation_code === 'Y'
                  ? 'Yes'
                  : claim.in_litigation_code === 'N'
                  ? 'No'
                  : claim.in_litigation_code
              }
            />
            <DetailRow label="Deductible" value={formatCurrency(claim.deductible_amt)} />
            <DetailRow label="Reserve Fees" value={formatCurrency(claim.reserve_fees)} />
          </dl>
        </div>
      </div>

      {policy && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <SectionHeading>Policy Information</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <dl>
              <DetailRow label="Policy Number" value={policy.policy_number} />
              <DetailRow label="Effective Date" value={formatDate(policy.effective_date)} />
              <DetailRow label="Expiration Date" value={formatDate(policy.expiration_date)} />
            </dl>
            <dl>
              <DetailRow label="Policy Limit" value={formatCurrency(policy.policy_limit)} />
              <DetailRow label="Deductible" value={formatCurrency(policy.deductible)} />
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Financials ──────────────────────────────────────────────────────────

function FinancialsTab({
  reserves,
  payments,
  transactions,
}: {
  reserves: ReservePaymentRow[];
  payments: ReservePaymentRow[];
  transactions: Transaction[];
}) {
  return (
    <div className="space-y-6">
      {/* Reserves */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <SectionHeading>Reserves</SectionHeading>
        {reserves.length === 0 ? (
          <p className="text-sm text-gray-500">No reserve data available.</p>
        ) : (
          <TableContainer>
            <thead>
              <tr>
                <Th>Type</Th>
                <Th>Description</Th>
                <Th right>Loss Amount</Th>
                <Th right>ALAE Amount</Th>
                <Th right>Transactions</Th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {reserves.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <Td>
                    <span className="font-medium">{row.type_code}</span>
                    {row.reserve_type && (
                      <span className="ml-1 text-gray-400">({row.reserve_type})</span>
                    )}
                  </Td>
                  <Td>{row.description || '—'}</Td>
                  <Td right>{formatAmount(row.loss_amount)}</Td>
                  <Td right>{formatAmount(row.alae_amount)}</Td>
                  <Td right>{row.transaction_count}</Td>
                </tr>
              ))}
            </tbody>
          </TableContainer>
        )}
      </div>

      {/* Payments */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <SectionHeading>Payments</SectionHeading>
        {payments.length === 0 ? (
          <p className="text-sm text-gray-500">No payment data available.</p>
        ) : (
          <TableContainer>
            <thead>
              <tr>
                <Th>Type</Th>
                <Th>Description</Th>
                <Th right>Loss Amount</Th>
                <Th right>ALAE Amount</Th>
                <Th right>Transactions</Th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {payments.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <Td>
                    <span className="font-medium">{row.type_code}</span>
                    {row.reserve_type && (
                      <span className="ml-1 text-gray-400">({row.reserve_type})</span>
                    )}
                  </Td>
                  <Td>{row.description || '—'}</Td>
                  <Td right>{formatAmount(row.loss_amount)}</Td>
                  <Td right>{formatAmount(row.alae_amount)}</Td>
                  <Td right>{row.transaction_count}</Td>
                </tr>
              ))}
            </tbody>
          </TableContainer>
        )}
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <SectionHeading>Recent Transactions</SectionHeading>
        {transactions.length === 0 ? (
          <p className="text-sm text-gray-500">No transactions available.</p>
        ) : (
          <TableContainer>
            <thead>
              <tr>
                <Th>Date</Th>
                <Th>Type</Th>
                <Th>Description</Th>
                <Th right>Amount</Th>
                <Th right>Loss</Th>
                <Th right>ALAE</Th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {transactions.map((tx, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <Td>{formatDate(tx.date)}</Td>
                  <Td>{tx.type_code}</Td>
                  <Td>{tx.description || '—'}</Td>
                  <Td right>
                    <span
                      className={
                        tx.amount < 0 ? 'text-red-600 font-medium' : 'text-green-700 font-medium'
                      }
                    >
                      {formatAmount(tx.amount)}
                    </span>
                  </Td>
                  <Td right>{formatAmount(tx.loss_amount)}</Td>
                  <Td right>{formatAmount(tx.alae_amount)}</Td>
                </tr>
              ))}
            </tbody>
          </TableContainer>
        )}
      </div>
    </div>
  );
}

// ─── Tab: AI Analysis ─────────────────────────────────────────────────────────

type AiAction = 'summary' | 'analysis' | 'draft';

interface AiState {
  loading: AiAction | null;
  results: Partial<Record<AiAction, AiResult>>;
  errors: Partial<Record<AiAction, string>>;
  draftType: string;
}

function AiTab({ claimId, coverageType, lob }: { claimId: string; coverageType?: string; lob?: string }) {
  const navigate = useNavigate();
  const [state, setState] = useState<AiState>({
    loading: null,
    results: {},
    errors: {},
    draftType: 'status_update',
  });

  const runAi = useCallback(
    async (action: AiAction) => {
      setState((prev) => ({
        ...prev,
        loading: action,
        errors: { ...prev.errors, [action]: undefined },
      }));

      try {
        let raw: any;
        if (action === 'summary') {
          raw = await api.summarizeClaim(claimId);
          setState((prev) => ({
            ...prev,
            loading: null,
            results: {
              ...prev.results,
              summary: {
                text: raw.summary,
                model: raw.model,
                latency_ms: raw.latency_ms,
                mock_mode: raw.mock_mode,
              },
            },
          }));
        } else if (action === 'analysis') {
          raw = await api.analyzeClaim(claimId);
          setState((prev) => ({
            ...prev,
            loading: null,
            results: {
              ...prev.results,
              analysis: {
                text: raw.analysis,
                model: raw.model,
                latency_ms: raw.latency_ms,
                mock_mode: raw.mock_mode,
              },
            },
          }));
        } else {
          raw = await api.draftCorrespondence(claimId, {
            correspondence_type: state.draftType,
          });
          setState((prev) => ({
            ...prev,
            loading: null,
            results: {
              ...prev.results,
              draft: {
                text: raw.draft,
                model: raw.model,
                latency_ms: 0,
                mock_mode: false,
              },
            },
          }));
        }
      } catch (err: any) {
        setState((prev) => ({
          ...prev,
          loading: null,
          errors: {
            ...prev.errors,
            [action]: err.message || 'An error occurred.',
          },
        }));
      }
    },
    [claimId, state.draftType]
  );

  const AiResultCard = ({
    action,
    label,
  }: {
    action: AiAction;
    label: string;
  }) => {
    const result = state.results[action];
    const error = state.errors[action];
    const isLoading = state.loading === action;

    if (!result && !error && !isLoading) return null;

    return (
      <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
        {/* Result header */}
        {(result || error) && (
          <div className="flex items-center justify-between px-5 pt-4 pb-2">
            <h4 className="text-sm font-semibold text-gray-800">{label}</h4>
            {result && (
              <span className="text-xs text-gray-400">
                {result.model}
                {result.latency_ms > 0 && ` · ${(result.latency_ms / 1000).toFixed(1)}s`}
                {result.mock_mode && ' · mock'}
              </span>
            )}
          </div>
        )}

        {/* Loading state — prominent for the 7-10s wait */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center gap-4 py-10 px-5">
            <div className="relative">
              <svg
                className="animate-spin h-10 w-10 text-blue-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-20"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                />
                <path
                  className="opacity-80"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-blue-700">
                {action === 'summary'
                  ? 'Generating AI Summary...'
                  : action === 'analysis'
                  ? 'Running Status Analysis...'
                  : 'Drafting Correspondence...'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Claude is analyzing this claim. This typically takes 7–15 seconds.
              </p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="mx-5 mb-4 mt-1 flex items-start gap-3 rounded-lg bg-red-50 border border-red-200 px-4 py-3">
            <svg
              className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
            <div>
              <p className="text-sm font-semibold text-red-800">AI request failed</p>
              <p className="text-xs text-red-600 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {/* Result content */}
        {result && !isLoading && (
          <div className="px-5 pb-5">
            <AiTextBlock text={result.text} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
      {/* AI Summary */}
      <div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => runAi('summary')}
            disabled={state.loading !== null}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <span>AI Summary</span>
          </button>
          <p className="text-xs text-gray-500">Generate a plain-language summary of this claim.</p>
        </div>
        <AiResultCard action="summary" label="Claim Summary" />
      </div>

      <div className="border-t border-gray-100" />

      {/* Status Analysis */}
      <div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => runAi('analysis')}
            disabled={state.loading !== null}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <span>Status Analysis</span>
          </button>
          <p className="text-xs text-gray-500">Analyze claim status, reserves, and risk factors.</p>
        </div>
        <AiResultCard action="analysis" label="Status Analysis" />
      </div>

      <div className="border-t border-gray-100" />

      {/* Draft Correspondence */}
      <div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => runAi('draft')}
            disabled={state.loading !== null}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <span>Draft Correspondence</span>
          </button>
          <select
            value={state.draftType}
            onChange={(e) =>
              setState((prev) => ({ ...prev, draftType: e.target.value }))
            }
            disabled={state.loading !== null}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="status_update">Status Update</option>
            <option value="reservation_of_rights">Reservation of Rights</option>
            <option value="coverage_denial">Coverage Denial</option>
            <option value="settlement_offer">Settlement Offer</option>
            <option value="closing_letter">Closing Letter</option>
          </select>
          <p className="text-xs text-gray-500">Generate a draft letter for this claim.</p>
        </div>
        <AiResultCard action="draft" label="Draft Correspondence" />
      </div>

      <div className="border-t border-gray-100" />

      {/* Precedent Search shortcut */}
      <div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              const params = new URLSearchParams();
              if (coverageType) params.set('coverage', coverageType);
              if (lob) params.set('lob', lob);
              navigate(`/precedent-search?${params.toString()}`);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Find Similar Claims
          </button>
          <p className="text-xs text-gray-500">Search historical claims for precedent on similar situations.</p>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Parties ─────────────────────────────────────────────────────────────

function PartiesTab({ parties }: { parties: Party[] }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <SectionHeading>Parties</SectionHeading>
      {parties.length === 0 ? (
        <p className="text-sm text-gray-500">No parties on record for this claim.</p>
      ) : (
        <TableContainer>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Location</Th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {parties.map((p) => (
              <tr key={p.party_id} className="hover:bg-gray-50">
                <Td>
                  <span className="font-medium text-gray-900">
                    {[p.first_name, p.last_name].filter(Boolean).join(' ') || '—'}
                  </span>
                </Td>
                <Td>
                  {p.city && p.state
                    ? `${p.city}, ${p.state}`
                    : p.city || p.state || '—'}
                </Td>
              </tr>
            ))}
          </tbody>
        </TableContainer>
      )}
    </div>
  );
}

// ─── Tab: Diary ───────────────────────────────────────────────────────────────

function DiaryTab({ diary }: { diary: DiaryEntry[] }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <SectionHeading>Diary Entries</SectionHeading>
      {diary.length === 0 ? (
        <p className="text-sm text-gray-500">No diary entries for this claim.</p>
      ) : (
        <TableContainer>
          <thead>
            <tr>
              <Th>Date</Th>
              <Th>Type</Th>
              <Th>Description</Th>
              <Th>Completed</Th>
              <Th>Assigned To</Th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {diary.map((entry, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <Td>{formatDate(entry.diary_date)}</Td>
                <Td>
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                    {entry.diary_type || '—'}
                  </span>
                </Td>
                <Td>
                  <span className="block max-w-sm text-gray-700 whitespace-normal leading-snug">
                    {entry.description || '—'}
                  </span>
                </Td>
                <Td>
                  {entry.completed_date ? (
                    <span className="text-green-600 font-medium">
                      {formatDate(entry.completed_date)}
                    </span>
                  ) : (
                    <span className="text-amber-600">Pending</span>
                  )}
                </Td>
                <Td>{entry.assigned_to || '—'}</Td>
              </tr>
            ))}
          </tbody>
        </TableContainer>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ClaimDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ClaimDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [changeFormOpen, setChangeFormOpen] = useState(false);
  const [changeHistoryKey, setChangeHistoryKey] = useState(0);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    api
      .getClaim(id)
      .then((result: ClaimDetailData) => {
        setData(result);
        setLoading(false);
      })
      .catch((err: any) => {
        setError(err.message || 'Failed to load claim.');
        setLoading(false);
      });
  }, [id]);

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-500">
          <svg
            className="animate-spin h-8 w-8 text-blue-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8H4z"
            />
          </svg>
          <p className="text-sm">Loading claim...</p>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────────
  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-4xl mb-3">!</div>
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Failed to Load Claim</h2>
          <p className="text-sm text-gray-500 mb-6">{error || 'Claim data is unavailable.'}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const { claim, financials, reserves, payments, transactions, parties, policy, diary } = data;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* ── Header ──────────────────────────────────────────────────────────── */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium mb-4 transition-colors"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            Dashboard
          </button>

          <div className="flex flex-wrap items-start gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {claim.tier_claim_num || claim.fh_claim_num}
              </h1>
              {claim.tier_claim_num && claim.fh_claim_num && (
                <p className="text-sm text-gray-500 mt-0.5">FH: {claim.fh_claim_num}</p>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <StatusBadge status={claim.open_close_status} />
              <button
                onClick={() => setChangeFormOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Request Change
              </button>
            </div>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
            {claim.handler_name && (
              <span>
                <span className="font-medium text-gray-700">Handler:</span>{' '}
                {claim.handler_name}
              </span>
            )}
            {claim.company_name && (
              <span>
                <span className="font-medium text-gray-700">Company:</span>{' '}
                {claim.company_name}
              </span>
            )}
            {claim.insured_name && (
              <span>
                <span className="font-medium text-gray-700">Insured:</span>{' '}
                {claim.insured_name}
              </span>
            )}
          </div>
        </div>

        {/* ── Financial Summary Cards ──────────────────────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <FinancialCard label="Total Outstanding" value={financials.total_outstanding} />
          <FinancialCard label="Total Paid" value={financials.total_paid} />
          <FinancialCard label="Total Recovered" value={financials.total_recovered} />
          <FinancialCard label="Total Incurred" value={financials.total_incurred} />
        </div>

        {/* ── Tabs ─────────────────────────────────────────────────────────────── */}
        <div className="mb-4 border-b border-gray-200">
          <nav className="-mb-px flex gap-1 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`shrink-0 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* ── Tab Content ──────────────────────────────────────────────────────── */}
        {activeTab === 'overview' && <OverviewTab claim={claim} policy={policy} />}
        {activeTab === 'financials' && (
          <FinancialsTab
            reserves={reserves}
            payments={payments}
            transactions={transactions}
          />
        )}
        {activeTab === 'ai' && id && <AiTab claimId={id} coverageType={claim.coverage_type_code} lob={claim.lob_id || undefined} />}
        {activeTab === 'parties' && <PartiesTab parties={parties} />}
        {activeTab === 'diary' && <DiaryTab diary={diary} />}
        {activeTab === 'changes' && id && <ChangeHistory key={changeHistoryKey} claimId={id} />}
        {activeTab === 'time' && id && <TimeTab claimId={id} claimReference={claim.tier_claim_num || claim.fh_claim_num} />}
      </div>

      {/* Change Request Modal */}
      {id && (
        <ChangeRequestForm
          claimId={id}
          claimNumber={claim.tier_claim_num || claim.fh_claim_num}
          isOpen={changeFormOpen}
          onClose={() => setChangeFormOpen(false)}
          onSuccess={() => {
            setChangeHistoryKey((k) => k + 1);
            setActiveTab('changes');
          }}
        />
      )}
    </div>
  );
}
